import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Stack, Typography } from '@mui/material';
import { BatteryChargingFull, Bolt, Business, SolarPower } from '@mui/icons-material';
import { useEnergy } from '../context/EnergyContext';
import { formatValue } from '../utils/energyViewModel';
import InstallationEnergyDiagram from '../components/energy/InstallationEnergyDiagram';
import EmsDispatchDialog from '../components/energy/EmsDispatchDialog';
import {
  EnergyPageHeader,
  EnergyStatusChip,
  MetricTile,
  ProvenanceNote,
  SectionCard,
} from '../components/energy/EnergyUi';

const BALANCE_ASSETS = [
  { key: 'solarKw', label: 'Solar generation', color: '#f57c00', icon: <SolarPower /> },
  { key: 'bessKw', label: 'Battery net', color: '#388e3c', icon: <BatteryChargingFull /> },
  { key: 'gridKw', label: 'Grid', color: '#1976d2', icon: <Bolt /> },
  { key: 'loadKw', label: 'Customer load', color: '#7b1fa2', icon: <Business /> },
];

const BalanceAsset = ({ asset, model }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
    <Box sx={{ color: asset.color, display: 'flex' }}>{asset.icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>{asset.label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatValue(model.power[asset.key], 'kW')}
      </Typography>
    </Box>
  </Stack>
);

const PowerBalance = ({ model }) => (
  <Paper
    variant="outlined"
    sx={{
      mb: 2,
      p: { xs: 1.5, md: 2 },
      borderLeft: 4,
      borderLeftColor: model.power.balanced === false ? 'warning.main' : 'primary.main',
      background: 'linear-gradient(90deg, rgba(25,118,210,0.045), rgba(255,255,255,0))',
    }}
  >
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
      <Box>
        <Typography variant="h6" fontWeight={700}>Instantaneous power balance</Typography>
        <Typography variant="caption" color="text.secondary">Battery and grid values preserve import and export direction.</Typography>
      </Box>
      <EnergyStatusChip
        status={model.power.balanced === true ? 'normal' : model.power.balanced === false ? 'warning' : 'unavailable'}
        label={model.power.balanced === true ? 'Balanced' : model.power.balanced === false ? 'Outside tolerance' : '-'}
      />
    </Stack>
    <Grid container spacing={1.25} alignItems="center">
      {BALANCE_ASSETS.map((asset) => (
        <Grid item xs={6} md key={asset.key}>
          <BalanceAsset asset={asset} model={model} />
        </Grid>
      ))}
      <Grid item xs={12} md="auto">
        <Paper variant="outlined" sx={{ px: 1.5, py: 0.75, bgcolor: 'background.paper', minWidth: 150 }}>
          <Typography variant="caption" color="text.secondary">Residual / tolerance</Typography>
          <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatValue(model.power.residualKw, 'kW')} / {formatValue(model.power.toleranceKw, 'kW')}
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  </Paper>
);

const money = (value, currency = 'USD', precision = 2) => {
  if (value === null || value === undefined || value === '' || !Number.isFinite(Number(value))) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: precision, maximumFractionDigits: precision }).format(Number(value));
};

const billingMonthLabel = (period) => {
  if (!period) return null;
  if (typeof period === 'string') return period;
  const start = new Date(period.start);
  const end = new Date(period.end);
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return null;
  const timezone = period.timezone || 'America/New_York';
  const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: timezone });
  return `${formatter.formatRange(start, end)} (${timezone})`;
};

const LeaseEconomics = ({ economics = {} }) => (
  <SectionCard title="Economics" subtitle={economics.proposalStatus === 'contractual' ? 'Monthly lease and actual production' : 'Proposed 15-year lease and actual production'}>
    <Grid container spacing={1}>
      <Grid item xs={6}><MetricTile label={economics.proposalStatus === 'contractual' ? 'Monthly lease payment' : 'Proposed monthly lease payment'} value={money(economics.monthlyLeasePayment, economics.currency)} unit="/month" accent="#455a64" /></Grid>
      <Grid item xs={6}><MetricTile label="Separate operating cost" value={money(economics.monthlyOperatingCost, economics.currency)} unit="/month" accent="#455a64" /></Grid>
      <Grid item xs={6}><MetricTile label="Total system cost" value={money(economics.monthlySystemCost, economics.currency)} unit="/month" accent="#455a64" /></Grid>
      <Grid item xs={6}><MetricTile label="Daily system cost" value={money(economics.dailySystemCost, economics.currency)} unit="/day" accent="#455a64" /></Grid>
      <Grid item xs={6}><MetricTile label="System cost run rate" value={money(economics.currentSystemCostRate, economics.currency, 4)} unit="/h" accent="#455a64" helper="Lease and operating costs accrued per hour" /></Grid>
      <Grid item xs={6}><MetricTile label="Cost this month" value={money(economics.accruedMonthCost, economics.currency)} accent="#455a64" helper="Accrued through the displayed period" /></Grid>
      <Grid item xs={6}><MetricTile label="Current generation cost" value={money(economics.currentGenerationCostPerKwh, economics.currency, 4)} unit="/kWh" accent="#455a64" helper="System cost run rate / current solar output" /></Grid>
      <Grid item xs={6}><MetricTile label="Average generation cost this month" value={money(economics.averageGenerationCostThisMonthPerKwh, economics.currency, 4)} unit="/kWh" accent="#455a64" helper="Accrued cost / accepted monthly solar energy" /></Grid>
    </Grid>
    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
      {economics.assetScope || 'Lease scope: -'}
      {economics.billingMonth ? ` · Billing month: ${billingMonthLabel(economics.billingMonth) || '-'}` : ''}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block">
      The lease payment includes capital recovery. Operating costs appear separately when excluded from the lease. Fixed costs continue at zero production.
    </Typography>
    {economics.coverage && <Typography variant="caption" color="text.secondary" display="block">
      Monthly production coverage: {formatValue(economics.coverage.coveragePct, '%')}. A monthly unit cost requires energy and accrued cost covering the same period.
    </Typography>}
  </SectionCard>
);

function DashboardView() {
  const { model, loading } = useEnergy();
  const todayEnergy = model.todayEnergy || model.energy;
  const proposedLease = model.costs.allocatedLeaseRatePerHour == null && model.leaseEconomics?.proposalStatus === 'proposed';
  const telemetryProvenance = /simulat|demo/.test(String(model.meta.sourceType || '').toLowerCase()) ? 'Simulated' : 'Observed';
  const [emsOpen, setEmsOpen] = useState(false);
  const [eventsOpen, setEventsOpen] = useState(false);
  return (
    <Box>
      <EnergyPageHeader title="Account Dashboard" subtitle="Integrated solar, storage, load, EMS, and utility operating view" showRange={false}
        actions={<Button size="small" variant="outlined" onClick={() => setEmsOpen(true)}>EMS details</Button>} />
      {loading && <Alert severity="info" sx={{ mb: 2 }}>Loading energy data…</Alert>}
      <PowerBalance model={model} />
      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            <SectionCard title="Operational telemetry" subtitle="Current equipment and interconnection state"
              action={<Button size="small" onClick={() => setEventsOpen(true)}>Events{model.events.length ? ` (${model.events.length})` : ''}</Button>}>
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Solar generation" value={model.power.solarKw} unit="kW" accent="#f57c00" provenance={telemetryProvenance} /></Grid>
                <Grid item xs={6}><MetricTile label="Customer load" value={model.power.loadKw} unit="kW" accent="#7b1fa2" provenance={telemetryProvenance} /></Grid>
                <Grid item xs={6}><MetricTile label="Battery SOC" value={model.bess.socPct} unit="%" accent="#388e3c" provenance={telemetryProvenance} /></Grid>
                <Grid item xs={6}><MetricTile label="Usable BESS capacity" value={model.bess.usableCapacityKwh} unit="kWh" accent="#388e3c" /></Grid>
                <Grid item xs={6}><MetricTile label="Last completed interval demand" value={model.load.intervalDemandKw} unit="kW" accent="#7b1fa2" provenance="Calculated" /></Grid>
                <Grid item xs={6}><MetricTile label="PCC frequency" value={model.pcc.frequencyHz} unit="Hz" accent="#1976d2" provenance={telemetryProvenance} /></Grid>
              </Grid>
              {model.events.some((event) => event.severity === 'fault') && <Alert severity="error" sx={{ mt: 1 }}>Active equipment faults reported. Open Events for details.</Alert>}
            </SectionCard>

            <SectionCard title="Today's cumulative energy" subtitle="Import, export, charge and discharge totals">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Generated" value={todayEnergy.generatedKwh} measure={model.measures?.todayEnergy?.generatedKwh} unit="kWh" accent="#f57c00" provenance={model.measures?.todayEnergy?.generatedKwh?.sourceKind === 'estimated' ? 'Estimated' : 'Register-derived'} /></Grid>
                <Grid item xs={6}><MetricTile label="Consumed" value={todayEnergy.consumedKwh} measure={model.measures?.todayEnergy?.consumedKwh} unit="kWh" accent="#7b1fa2" provenance="Calculated" /></Grid>
                <Grid item xs={6}><MetricTile label="Imported" value={todayEnergy.importedKwh} measure={model.measures?.todayEnergy?.importedKwh} unit="kWh" accent="#1976d2" /></Grid>
                <Grid item xs={6}><MetricTile label="Exported" value={todayEnergy.exportedKwh} measure={model.measures?.todayEnergy?.exportedKwh} unit="kWh" accent="#0288d1" /></Grid>
                <Grid item xs={6}><MetricTile label="Battery charged" value={todayEnergy.chargedKwh} measure={model.measures?.todayEnergy?.chargedKwh} unit="kWh" accent="#388e3c" /></Grid>
                <Grid item xs={6}><MetricTile label="Battery discharged" value={todayEnergy.dischargedKwh} measure={model.measures?.todayEnergy?.dischargedKwh} unit="kWh" accent="#2e7d32" /></Grid>
              </Grid>
              <ProvenanceNote>
                Energy reported by the inverter and energy estimated from power readings are tracked separately. Inverter-reported energy today: {formatValue(todayEnergy.inverterRegisterKwh, 'kWh')}; estimated energy today: {formatValue(todayEnergy.sampledEstimateKwh, 'kWh')}.
              </ProvenanceNote>
            </SectionCard>
            <SectionCard title="Current account costs" subtitle="Energy prices per kWh; cost run rates per hour">
              <Grid container spacing={1}>
                <Grid item xs={6}><MetricTile label="Import energy price" value={model.costs.importEnergyPricePerKwh} precision={4} unit={`${model.costs.currency || 'Currency'}/kWh`} measure={model.measures?.costs?.current?.importEnergyPricePerKwh} helper="Includes energy riders and applicable tax; excludes fixed and demand charges" /></Grid>
                <Grid item xs={6}><MetricTile label="Export energy credit" value={model.costs.exportCreditPerKwh} precision={4} unit={`${model.costs.currency || 'Currency'}/kWh`} measure={model.measures?.costs?.current?.exportCreditPerKwh} helper={model.measures?.costs?.current?.exportCreditPerKwh?.taxApplied === false ? 'No VAT on exports' : undefined} /></Grid>
                <Grid item xs={6}><MetricTile label="Utility cost run rate" value={model.costs.utilityRatePerHour} unit={`${model.costs.currency || 'Currency'}/h`} measure={model.measures?.costs?.current?.utilityEnergyCostRate} helper="Current grid power × energy price; exports reduce cost" /></Grid>
                <Grid item xs={6}><MetricTile label="Modeled generation cost run rate" value={model.costs.modeledGenerationRatePerHour} unit={`${model.costs.currency || 'Currency'}/h`} measure={model.measures?.costs?.current?.modeledGenerationCostRate} /></Grid>
                <Grid item xs={6}><MetricTile label={proposedLease ? 'Proposed lease cost run rate' : 'Lease cost run rate'}
                  value={proposedLease ? model.leaseEconomics.currentLeaseCostRate : model.costs.allocatedLeaseRatePerHour}
                  provenance={proposedLease ? 'Proposed' : undefined} unit={`${model.costs.currency || 'Currency'}/h`}
                  measure={proposedLease ? undefined : model.measures?.costs?.current?.allocatedLeaseCostRate} /></Grid>
                <Grid item xs={6}><MetricTile label="Modeled LCOE" value={model.costs.modeledLcoePerKwh} unit={`${model.costs.currency || 'Currency'}/kWh`} measure={model.measures?.costs?.summary?.modeledLcoe} /></Grid>
              </Grid>
            </SectionCard>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <InstallationEnergyDiagram model={model} onOpenEms={() => setEmsOpen(true)} />
            <LeaseEconomics economics={model.leaseEconomics} />
          </Stack>
        </Grid>
      </Grid>
      <EmsDispatchDialog open={emsOpen} onClose={() => setEmsOpen(false)} model={model} />
      <Dialog open={eventsOpen} onClose={() => setEventsOpen(false)} fullWidth maxWidth="sm" aria-labelledby="energy-events-title">
        <DialogTitle id="energy-events-title">Operational events</DialogTitle>
        <DialogContent>
          <EnergyStatusChip status={model.meta.quality} />
          {model.events.length ? model.events.map((event, index) => (
            <Alert key={event.id || index} severity={event.severity === 'fault' ? 'error' : 'warning'} sx={{ mt: 1 }}>
              {event.message || event.code || 'Energy event'}
            </Alert>
          )) : <Typography variant="body2" sx={{ mt: 1 }}>{model.meta.quality === 'unavailable' ? 'No event data received.' : 'No active events were returned.'}</Typography>}
        </DialogContent>
        <DialogActions><Button onClick={() => setEventsOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default DashboardView;
